/**
 * /ndt-inspection/technicians/certs/:id — one technician certification, its
 * document, and the writes on it (GET / PATCH / DELETE
 * /ndt-inspection/technicians/certs/:id).
 *
 * The document goes through the presigned flow with entityType 'ndt_cert'
 * (hooks/useNdtInspection.ts useAttachNdtCertDocument): presign, PUT, confirm,
 * then PATCH document_upload_id. Opening it mints a short-lived download
 * token; a presigned URL is never kept.
 */
import React, { useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { CalendarClock, FileText, Pencil, ShieldCheck, Trash2, Upload } from 'lucide-react';
import { toast } from 'sonner';
import { api, uploadDownloadUrl } from '../../api/client';
import { PageHeaderBand } from '../../components/layout/PageHeaderBand';
import { LoadFailureNotice } from '../../components/ui/LoadFailureNotice';
import { useAuth } from '../../contexts/AuthContext';
import { useAttachNdtCertDocument, useDeleteNdtCert, useNdtCert } from '../../hooks/useNdtInspection';
import { queryFailureError, queryLoadFailure } from '../../lib/load-failure';
import { canAdminNdt, daysHint, ndtErrorMessage, ndtExpirySkin, ndtLevelLabel } from '../../lib/ndt-inspection';
import { CertDialog, ConfirmDialog } from './NdtDialogs';
import { isNotFound, NDT_SECONDARY, NdtBadge, NdtEmpty, NdtLoading, NdtPanel, NdtSpec, ndtDay } from './NdtBits';

export default function TechnicianCertDetailScreen() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const query = useNdtCert(id);
  const attach = useAttachNdtCertDocument();
  const remove = useDeleteNdtCert();
  const fileRef = useRef<HTMLInputElement>(null);
  const [editing, setEditing] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [opening, setOpening] = useState(false);

  const cert = query.data?.data ?? null;
  const failure = queryLoadFailure(query);
  const notFound = isNotFound(queryFailureError(query));
  const canEdit = canAdminNdt(user?.role) && !!cert;

  const onFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file || !cert) return;
    attach.mutate(
      { certId: cert.id, file },
      {
        onSuccess: () => toast.success('Document attached'),
        onError: (err) => toast.error(err instanceof Error && !('response' in err) ? err.message : ndtErrorMessage(err, 'The document could not be attached.')),
      },
    );
  };

  const openDocument = async () => {
    if (!cert?.documentUploadId) return;
    setOpening(true);
    try {
      const res = await api.uploads.downloadToken(cert.documentUploadId);
      const token = (res.data as { data?: { token?: unknown } } | null)?.data?.token;
      if (typeof token !== 'string' || !token) throw new Error('No download token returned');
      window.open(uploadDownloadUrl(cert.documentUploadId, token), '_blank', 'noopener');
    } catch (err) {
      toast.error(ndtErrorMessage(err, 'The document could not be opened.'));
    } finally {
      setOpening(false);
    }
  };

  return (
    <div data-testid="ndt-cert-detail-page" className="animate-fade-in min-h-full bg-fieldiq-dark">
      <PageHeaderBand
        eyebrow="NDT inspection · Certification"
        title={cert ? `${cert.userName || '—'} · ${cert.method}` : query.isLoading ? 'Certification' : '—'}
        onBack={() => navigate(-1)}
        backLabel="Back"
        actions={
          canEdit ? (
            <button type="button" onClick={() => setEditing(true)} className="fi-action fi-action--standard fi-action--primary">
              <Pencil className="size-4" aria-hidden="true" />
              Edit
            </button>
          ) : undefined
        }
        stats={[
          {
            icon: <ShieldCheck className="size-4" aria-hidden="true" />,
            label: 'Status',
            tone: cert?.status === 'valid' ? 'success' : cert?.status === 'none' ? 'neutral' : 'accent',
            loading: query.isLoading,
            value: cert ? ndtExpirySkin(cert.status).label : '—',
          },
          {
            icon: <CalendarClock className="size-4" aria-hidden="true" />,
            label: 'Expires',
            sub: cert && cert.status !== 'none' ? daysHint(cert.daysLeft, 'ago') ?? undefined : undefined,
            tone: 'neutral',
            loading: query.isLoading,
            value: ndtDay(cert?.expiresOn),
          },
        ]}
      />

      <section className="px-[var(--fi-page-inline)] py-[var(--fi-page-block)] pb-[var(--bottom-chrome)]">
        <div className="mx-auto max-w-[1920px] space-y-4">
          {query.isLoading ? (
            <NdtLoading label="Loading certification" />
          ) : notFound || (!failure && !cert) ? (
            <NdtPanel eyebrow="Certification" title="Certification">
              <NdtEmpty>This certification was not found. It may have been deleted.</NdtEmpty>
            </NdtPanel>
          ) : failure || !cert ? (
            <NdtPanel eyebrow="Certification" title="Certification">
              <LoadFailureNotice error={queryFailureError(query)} kind={failure ?? 'unreachable'} subject="this certification" />
            </NdtPanel>
          ) : (
            <>
              <NdtPanel eyebrow="Credential" title="Details" testId="ndt-cert-details">
                <dl className="grid gap-4 px-4 py-4 sm:grid-cols-2 lg:grid-cols-4">
                  <NdtSpec label="Technician" value={cert.userName || '—'} />
                  <NdtSpec label="Method" value={cert.methodLabel || cert.method || '—'} />
                  <NdtSpec label="Level" value={ndtLevelLabel(cert.level)} />
                  <NdtSpec label="Scheme" value={cert.schemeLabel || cert.scheme || '—'} />
                  <NdtSpec label="Sector" value={cert.sector || '—'} />
                  <NdtSpec label="Certificate #" value={cert.certificateNumber || '—'} mono />
                  <NdtSpec label="Issuing body" value={cert.issuingBody || '—'} />
                  <NdtSpec label="Issued" value={ndtDay(cert.issuedOn)} />
                  <NdtSpec label="Expires" value={ndtDay(cert.expiresOn)} />
                  <NdtSpec label="Status" value={<NdtBadge skin={ndtExpirySkin(cert.status)} value={cert.status} />} />
                </dl>
                {cert.notes && <p className="border-t border-fieldiq-border px-4 py-3 text-sm text-fieldiq-text">{cert.notes}</p>}
              </NdtPanel>

              <NdtPanel
                eyebrow="Document"
                title="Certificate document"
                testId="ndt-cert-document"
                actions={
                  canEdit ? (
                    <>
                      <input ref={fileRef} type="file" accept="application/pdf,image/*" className="hidden" onChange={onFile} data-testid="ndt-cert-file" />
                      <button type="button" className={NDT_SECONDARY} disabled={attach.isPending} onClick={() => fileRef.current?.click()}>
                        <Upload className="size-4" aria-hidden="true" />
                        {attach.isPending ? 'Uploading…' : cert.documentUploadId ? 'Replace' : 'Upload'}
                      </button>
                    </>
                  ) : undefined
                }
              >
                {cert.documentUploadId ? (
                  <div className="px-4 py-4">
                    <button type="button" className={NDT_SECONDARY} onClick={openDocument} disabled={opening}>
                      <FileText className="size-4" aria-hidden="true" />
                      {opening ? 'Opening…' : 'Open document'}
                    </button>
                  </div>
                ) : (
                  <NdtEmpty>No document is attached.</NdtEmpty>
                )}
              </NdtPanel>

              {canEdit && (
                <div className="flex justify-end">
                  <button type="button" className={NDT_SECONDARY} onClick={() => { setDeleteError(null); setDeleting(true); }}>
                    <Trash2 className="size-4" aria-hidden="true" />
                    Delete certification
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </section>

      {cert && canEdit && <CertDialog open={editing} onClose={() => setEditing(false)} cert={cert} />}
      {cert && canEdit && (
        <ConfirmDialog
          open={deleting}
          title="Delete certification"
          body={`Remove ${cert.userName || 'this technician'}'s ${cert.methodLabel || cert.method} certification from the register?`}
          confirmLabel="Delete"
          pending={remove.isPending}
          error={deleteError}
          onClose={() => setDeleting(false)}
          onConfirm={() =>
            remove.mutate(cert.id, {
              onSuccess: () => {
                toast.success('Certification deleted');
                navigate('/ndt-inspection/technicians', { replace: true });
              },
              onError: (err) => setDeleteError(ndtErrorMessage(err, 'The certification could not be deleted.')),
            })
          }
        />
      )}
    </div>
  );
}
